import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Footer } from './Footer';
import { HourGrid } from './HourGrid';
import { EventBlockGrid } from './EventBlockGrid';
import { Row } from '@amzn/atoz-web/Row';
import { Col } from '@amzn/atoz-web/Col';
import { WEEKLY_SCHEDULE_CONTAINER, WEEKLY_SCHEDULE_GRID_CONTAINER } from './testids.consts';
import { styledWithTheme } from '@amzn/atoz-web/Theme';
import useMutationObserver from '@rooks/use-mutation-observer';
import { defaultEmitErrorLog, setLuxonLocale, setLuxonTimeZone, getDisplayDetailsForEvents, createEventsByDateMap, getWeekRangeFromWeekRow, START_DATE_ATTRIBUTE_NAME, getScrollbarWidth, enablePageVerticalScroll, getEventBlockId, scrollScheduleGridTo } from './utils';
import { WeekRow } from './WeekRow';
import { Header } from './Header';
import { DimensionConstants, WeeklyScheduleDimensions } from './dimensions.consts';
export function WeeklySchedule(_a) {
    var eventTypeMapping = _a.eventTypeMapping, fallbackEventDetails = _a.fallbackEventDetails, selectedDate = _a.selectedDate, updateDateAction = _a.updateDateAction, nextWeekAriaLabel = _a.nextWeekAriaLabel, prevWeekAriaLabel = _a.prevWeekAriaLabel, _b = _a.events, events = _b === void 0 ? [] : _b, _c = _a.emitErrorLog, emitErrorLog = _c === void 0 ? defaultEmitErrorLog : _c, locale = _a.locale, timeZone = _a.timeZone;
    useEffect(function () {
        DimensionConstants.scrollbar.width = getScrollbarWidth();
    }, []);
    useEffect(function () {
        if (locale) {
            setLuxonLocale(locale);
        }
        if (timeZone) {
            setLuxonTimeZone(timeZone, emitErrorLog);
        }
    }, [locale, timeZone, emitErrorLog]);
    var weekStartDateRef = useRef(null);
    var _d = useState(), weekRange = _d[0], setWeekRange = _d[1];
    var onWeekRangeMutated = function (mutationList) {
        for (var _i = 0, mutationList_1 = mutationList; _i < mutationList_1.length; _i++) {
            var _a = mutationList_1[_i], type = _a.type, attributeName = _a.attributeName, target = _a.target;
            if (type === 'attributes' &&
                attributeName === START_DATE_ATTRIBUTE_NAME &&
                target instanceof Element) {
                setWeekRange(getWeekRangeFromWeekRow(target));
            }
        }
    };
    useMutationObserver(weekStartDateRef, onWeekRangeMutated);
    useEffect(function () {
        /*
            Upon resizing the screen, this component undergoes re-rendering, which resets the weekRange
            state to undefined. However, even after re-rendering, the week start date DOM element remains
            intact with accurate week range (no mutation). In this scenario, we want to reset the weekRange
            base on the week start date DOM element attribute.
        */
        var weekRowStartDateElements = document.querySelectorAll("[".concat(START_DATE_ATTRIBUTE_NAME, "]"));
        if (!weekRange && weekRowStartDateElements.length !== 0) {
            setWeekRange(getWeekRangeFromWeekRow(weekRowStartDateElements[0]));
        }
    }, [weekRange]);
    var detailedEvents = useMemo(function () {
        return getDisplayDetailsForEvents(events, eventTypeMapping, fallbackEventDetails, emitErrorLog);
    }, [events, emitErrorLog]);
    // TODO: Co-locate the computation of these two vars
    var weeklyEventsMap = useMemo(function () {
        if (!weekRange)
            return new Map();
        var weekRangeWithBuffer = {
            start: weekRange.start.minus({ day: 1 }),
            end: weekRange.end.plus({ day: 1 })
        };
        return createEventsByDateMap(detailedEvents, weekRangeWithBuffer);
    }, [detailedEvents, weekRange]);
    var weeklyDates = useMemo(function () {
        if (!weekRange)
            return [];
        var start = weekRange.start, end = weekRange.end;
        var datesPerWeek = [];
        for (var date = start; date < end; date = date.plus({ day: 1 })) {
            datesPerWeek.push(date.toISODate());
        }
        return datesPerWeek;
    }, [weekRange]);
    useEffect(function () {
        var selectedDateEvents = weeklyEventsMap.get(selectedDate.toISODate());
        if (selectedDateEvents && selectedDateEvents.length > 0) {
            scrollScheduleGridTo(getEventBlockId(selectedDateEvents[0]), emitErrorLog);
        }
    }, [selectedDate, weeklyEventsMap]);
    return (React.createElement(WeeklyScheduleWrapper, { dataTestId: WEEKLY_SCHEDULE_CONTAINER },
        React.createElement(Header, { updateDateAction: updateDateAction, weekRange: weekRange, nextWeekAriaLabel: nextWeekAriaLabel, prevWeekAriaLabel: prevWeekAriaLabel }),
        React.createElement(Col, { className: "schedule-view-container" },
            React.createElement(Row, { className: "weekRow-container", ref: weekStartDateRef },
                React.createElement(WeekRow, { selectedDate: selectedDate, onSelectedDateChange: updateDateAction, locale: locale, emitErrorLog: emitErrorLog })),
            React.createElement(Row, { id: WEEKLY_SCHEDULE_GRID_CONTAINER, dataTestId: WEEKLY_SCHEDULE_GRID_CONTAINER, height: WeeklyScheduleDimensions.gridContainer.height, tabIndex: 0, style: {
                    position: 'relative',
                    overflow: 'auto',
                    scrollBehavior: 'smooth'
                }, onMouseEnter: function () { return enablePageVerticalScroll(false); }, onMouseLeave: function () { return enablePageVerticalScroll(true); } },
                React.createElement(HourGrid, null),
                weekRange && (React.createElement(EventBlockGrid, { eventsByDateMap: weeklyEventsMap, weeklyDates: weeklyDates }))),
            React.createElement(Footer, { eventTypeMapping: eventTypeMapping }))));
}
var WeeklyScheduleWrapper = styledWithTheme(Col)(function (_a) {
    var theme = _a.theme;
    var scheduleViewContainer = WeeklyScheduleDimensions.scheduleViewContainer;
    return "\n        .schedule-view-container{\n            width: ".concat(scheduleViewContainer.width, "\n        }\n        .weekRow-container{\n            box-shadow: 0 5px 5px -5px ").concat(theme.selectors.color('neutral40'), ";\n            z-index: 1;\n        }\n    ");
});
